import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
  Avatar,
  Grid,
  Card,
  Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { projectEssentialApi, type ProjectMembersResponse } from '../services/api';

interface ProjectCoreTeamDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectName?: string | null;
}

interface MemberCardItemProps {
  member: ProjectMembersResponse['members'][0]['members'][0];
  getPhotoUrl: (photo: string | null | undefined) => string;
  getInitials: (name: string | null | undefined) => string;
}

function AssignedPeopleSection({
  label,
  people,
  getPhotoUrl,
  getInitials,
}: {
  label: string;
  people: NonNullable<MemberCardItemProps['member']['taggers']>;
  getPhotoUrl: MemberCardItemProps['getPhotoUrl'];
  getInitials: MemberCardItemProps['getInitials'];
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <Box sx={{ mt: 1, pt: 1.5, borderTop: '1px solid var(--color-border-stronger)' }}>
      <Box
        component="button"
        onClick={() => setExpanded(!expanded)}
        sx={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 0.5,
          color: 'var(--color-text-muted)',
          '&:hover': { color: 'var(--color-accent-strong)' },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 850,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {label} ({people.length})
        </Typography>
        {expanded ? (
          <ExpandLessIcon sx={{ fontSize: '1.25rem', color: 'var(--color-accent-strong)' }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: '1.25rem' }} />
        )}
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ pt: 1.5 }}>
          <Grid container spacing={1.5}>
            {people.map((person, personIdx) => (
              <Grid item xs={12} sm={6} md={4} key={`${person.userId}-${personIdx}`}>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Avatar
                      src={getPhotoUrl(person.photo)}
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: 'var(--color-border-strong)',
                        color: 'var(--color-text-muted)',
                        fontWeight: 800,
                        fontSize: '0.75rem',
                      }}
                    >
                      {getInitials(person.name)}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          color: 'var(--color-text-strong)',
                          display: 'block',
                          lineHeight: 1.2,
                        }}
                      >
                        {person.name || 'N/A'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'var(--color-text-muted)', fontSize: '0.68rem', fontWeight: 600 }}
                      >
                        CEA: {person.ceaNumber || 'N/A'}
                      </Typography>
                    </Box>
                  </Stack>

                  {person.mobile && (
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        component="a"
                        href={`tel:${person.mobile}`}
                        size="small"
                        sx={{
                          p: 0.5,
                          color: 'var(--color-text-muted)',
                          '&:hover': { color: 'var(--color-accent)' },
                        }}
                      >
                        <PhoneIcon sx={{ fontSize: '0.95rem' }} />
                      </IconButton>
                      <IconButton
                        component="a"
                        href={`https://wa.me/65${person.mobile.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        sx={{
                          p: 0.5,
                          color: '#25D366',
                          '&:hover': { bgcolor: 'rgba(37,211,102,0.1)' },
                        }}
                      >
                        <WhatsAppIcon sx={{ fontSize: '0.95rem' }} />
                      </IconButton>
                    </Stack>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
}

function MemberCardItem({ member, getPhotoUrl, getInitials }: MemberCardItemProps) {
  const taggers = member.taggers || [];
  const pcs = member.pcs || [];

  return (
    <Card
      sx={{
        p: 2.25,
        borderRadius: 2.5,
        border: '1px solid var(--color-border-strong)',
        bgcolor: 'var(--color-surface)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        transition: 'all 150ms ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
          borderColor: 'var(--color-accent-soft)',
        },
      }}
    >
      <Stack spacing={2}>
        {/* Main Member Row */}
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={getPhotoUrl(member.photo)}
              sx={{
                width: 50,
                height: 50,
                bgcolor: 'var(--color-accent-soft)',
                color: 'var(--color-accent)',
                fontWeight: 800,
                fontSize: '1rem',
                border: '2px solid var(--color-border)',
              }}
            >
              {getInitials(member.name)}
            </Avatar>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 800, color: 'var(--color-text-strong)', lineHeight: 1.3 }}>
                {member.name || 'N/A'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 700 }}>
                CEA: {member.ceaNumber || 'N/A'}
              </Typography>
            </Box>
          </Stack>

          {/* Member Quick Contact CTA */}
          {member.mobile && (
            <Stack direction="row" spacing={1}>
              <IconButton
                component="a"
                href={`tel:${member.mobile}`}
                size="small"
                sx={{
                  bgcolor: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text)',
                  '&:hover': { bgcolor: 'var(--color-accent-soft)', color: 'var(--color-accent-strong)' },
                }}
              >
                <PhoneIcon fontSize="small" />
              </IconButton>
              <IconButton
                component="a"
                href={`https://wa.me/65${member.mobile.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  bgcolor: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-border)',
                  color: '#25D366',
                  '&:hover': { bgcolor: 'rgba(37,211,102,0.12)' },
                }}
              >
                <WhatsAppIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Stack>

        {pcs.length > 0 ? (
          <AssignedPeopleSection
            label="Assigned PCs"
            people={pcs}
            getPhotoUrl={getPhotoUrl}
            getInitials={getInitials}
          />
        ) : null}

        {taggers.length > 0 ? (
          <AssignedPeopleSection
            label="Assigned Taggers"
            people={taggers}
            getPhotoUrl={getPhotoUrl}
            getInitials={getInitials}
          />
        ) : null}
      </Stack>
    </Card>
  );
}

export function ProjectCoreTeamDialog({
  open,
  onClose,
  projectId,
  projectName,
}: ProjectCoreTeamDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [membersData, setMembersData] = useState<ProjectMembersResponse | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    projectEssentialApi
      .getProjectMembers(projectId)
      .then((res) => {
        setMembersData(res);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load project members:', err);
        setError('Failed to load project taggers.');
        setLoading(false);
      });
  }, [open, projectId]);

  const getPhotoUrl = (photo: string | null | undefined) => {
    if (!photo) return '';
    if (/^https?:\/\//i.test(photo)) return photo;
    const photoPath = photo.startsWith('/') ? photo.slice(1) : photo;
    return `https://portal.huttonsgroup.com/${photoPath}`;
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'N/A';
    return name
      .split(' ')
      .map((part) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'var(--color-surface)',
          backgroundImage: 'none',
          boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Dialog Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2.25,
          borderBottom: '1px solid var(--color-border-strong)',
          bgcolor: 'var(--color-surface-elevated)',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PeopleIcon sx={{ color: 'var(--color-accent)', fontSize: '1.5rem' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 850, color: 'var(--color-text-strong)', lineHeight: 1.2 }}>
              Project Taggers
            </Typography>
            {projectName && (
              <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>
                {projectName}
              </Typography>
            )}
          </Box>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ color: 'var(--color-text-muted)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, minHeight: 250, bgcolor: 'var(--color-bg)', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, py: 6, gap: 2 }}>
            <CircularProgress sx={{ color: 'var(--color-accent)' }} size={40} />
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>
              Loading project taggers...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 6 }}>
            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>
              {error}
            </Typography>
          </Box>
        ) : !membersData || membersData.members.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 6 }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>
              No project taggers registered for this project.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={4.5}>
            {membersData.members.map((group, groupIdx) => (
              <Box key={groupIdx}>
                {/* Role Section Title */}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 850,
                    color: 'var(--color-accent-strong)',
                    textTransform: 'uppercase',
                    fontSize: '0.8rem',
                    letterSpacing: '0.08em',
                    mb: 2,
                    pb: 1,
                    borderBottom: '1px solid var(--color-border)',
                  }}
                >
                  {group.role}
                </Typography>

                {/* Role Group Members List */}
                <Grid container spacing={2.5}>
                  {group.members.map((member, memberIdx) => (
                    <Grid
                      item
                      xs={12}
                      sm={
                        (member.taggers && member.taggers.length > 0) ||
                        (member.pcs && member.pcs.length > 0)
                          ? 12
                          : 6
                      }
                      key={memberIdx}
                    >
                      <MemberCardItem
                        member={member}
                        getPhotoUrl={getPhotoUrl}
                        getInitials={getInitials}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
